/**
 * Percorsi e comandi dell'alberatura.
 *
 * Con l'albero a tre livelli la denominazione da sola **non identifica un
 * nodo**: l'albero adottato contiene omonimi perfetti — «Sanzioni per mancata o
 * incompleta comunicazione dei dati» esiste sotto Organizzazione e sotto
 * Personale, «Pari opportunita' e inclusione lavorativa nei contratti pubblici
 * PNRR e PNC» sotto Affidamento e sotto Esecuzione. Ovunque si offra un nodo da
 * scegliere (caselle del perimetro, tendine dei filtri, nomi accessibili dei
 * comandi) l'etichetta deve quindi portare la catena degli antenati, altrimenti
 * l'operatore sceglie fra due voci tipograficamente identiche.
 *
 * Il calcolo sta qui e non nelle pagine perche' le pagine che ne hanno bisogno
 * sono quattro e la regola e' una sola.
 */

import type { Nodo } from "../api/types";
import type { VoceCatalogo } from "../data/catalogo-nodi";
import { CATALOGO_NODI, catalogoFigli } from "../data/catalogo-nodi";
import { appiattisciAlberatura } from "./scadenze";

/**
 * Identificativi che il catalogo ANAC prevede come padri.
 *
 * Insieme calcolato una volta sola: `comandiNodo` e' invocato su ogni riga
 * dell'albero a ogni render — con 219 nodi una scansione del catalogo per riga
 * sarebbe una quarantina di migliaia di confronti per battuta di tastiera.
 */
const PADRI_A_CATALOGO: ReadonlySet<string> = new Set(
  CATALOGO_NODI.map((voce) => voce.id_padre).filter((id): id is string => id !== null),
);

/** Separatore dei percorsi, lo stesso in ogni superficie che li mostra. */
export const SEPARATORE_PERCORSO = " › ";

// --------------------------------------------------------------------------- //
// L'indirizzo pubblico di un nodo
//
// SORGENTE DI VERITA': `api/app/services/percorsi.py` — `SEGMENTO_AT` e
// `segmenti_percorso_nodo`, dove la formula vive **una volta sola** proprio
// perche' era gia' stata scritta due volte e le due copie erano divergute.
// L'indirizzo che conta e' quello che l'API memorizza sul nodo (`nodo.url`) e
// che la at_map dichiara: qui si ricompone la stessa formula per i soli casi in
// cui quell'indirizzo non c'e' ancora — l'anteprima di un contenuto in bozza —
// e per confrontare un URL pubblicato con il suo canonico.
//
// Il back-office non puo' importare il modulo dell'API: e' un altro servizio, e
// fra i due passa solo HTTP. Questa e' quindi una copia dichiarata, tenuta in un
// punto unico e allineata dalle asserzioni di `verifiche/dominio.verifica.ts`,
// che rileggono `percorsi.py` quando il monorepo e' a portata di mano.
// --------------------------------------------------------------------------- //

/** Segmento fisso degli indirizzi pubblici della sezione. */
export const SEGMENTO_AT = "amministrazione-trasparente";

/**
 * Profondita' dell'alberatura: sezione, sottosezione, obbligo.
 *
 * Coincide con `PROFONDITA_MASSIMA` di `api/app/services/percorsi.py` e con il
 * vincolo `ck_nodi_livello` sul modello dei nodi.
 */
export const PROFONDITA_MASSIMA = 3;

/** Gli identificativi sono in `snake_case`, gli indirizzi in `kebab-case`. */
const kebab = (identificativo: string): string => identificativo.replaceAll("_", "-");

/**
 * Segmenti dell'indirizzo, dalla radice della sezione al nodo.
 *
 * `antenati` sono gli identificativi **dalla sezione al padre**, vuoti per un
 * nodo di primo livello. Gli identificativi vuoti non producono segmenti vuoti:
 * un indirizzo si compone anche prima che il nodo sia noto, e
 * un doppio slash sarebbe un indirizzo che non esiste.
 */
export function segmentiPercorsoNodo(
  idNodo: string,
  antenati: readonly string[] = [],
): string[] {
  return [SEGMENTO_AT, ...antenati, idNodo].filter((pezzo) => pezzo !== "").map(kebab);
}

/**
 * Percorso pubblico del nodo, con la barra iniziale.
 *
 * E' la forma con cui `api/app/services/percorsi.py::percorso_nodo` dichiara il
 * riferimento verificabile dalle copie fuori processo.
 */
export function percorsoPubblicoNodo(idNodo: string, antenati: readonly string[] = []): string {
  return `/${segmentiPercorsoNodo(idNodo, antenati).join("/")}`;
}

/**
 * Indirizzo assoluto del nodo sul dominio dell'ente.
 *
 * Solo per l'anteprima: l'indirizzo autorevole e' `nodo.url`, che l'API compone
 * sul proprio `portale_base_url`. Senza dominio resta il percorso, che e'
 * comunque un indirizzo leggibile e non una stringa a meta'.
 */
export function urlPubblicoNodo(
  dominio: string | null | undefined,
  idNodo: string,
  antenati: readonly string[] = [],
): string {
  const percorso = percorsoPubblicoNodo(idNodo, antenati);
  return dominio ? `https://${dominio}${percorso}` : percorso;
}

/**
 * Identificativi degli antenati di un nodo, dalla sezione al padre.
 *
 * Risale `id_padre` su una mappa gia' in memoria, come
 * `api/app/services/percorsi.py::risolvi_antenati`. La guardia sui nodi gia'
 * visti ferma un ciclo nei dati; la risalita **non** si ferma a un conteggio di
 * passi, perche' troncarla scarterebbe la sezione, cioe' il primo segmento
 * dell'indirizzo.
 */
export function identificativiAntenati(
  perId: ReadonlyMap<string, Pick<Nodo, "id_nodo" | "id_padre">>,
  idPadre: string | null | undefined,
): string[] {
  const antenati: string[] = [];
  const visti = new Set<string>();
  let corrente = idPadre ? perId.get(idPadre) : undefined;
  while (corrente && !visti.has(corrente.id_nodo)) {
    visti.add(corrente.id_nodo);
    antenati.unshift(corrente.id_nodo);
    corrente = corrente.id_padre ? perId.get(corrente.id_padre) : undefined;
  }
  return antenati;
}

/** Catena delle denominazioni dalla sezione al nodo, indicizzata per `id_nodo`. */
export type CatenePerId = ReadonlyMap<string, readonly string[]>;

/**
 * Catena delle denominazioni di ciascun nodo, dalla radice al nodo incluso.
 *
 * La risalita usa `id_padre` invece della sola annidatura: cosi' la funzione
 * regge tanto l'alberatura annidata dell'API quanto un elenco appiattito. La
 * guardia sui nodi gia' visti evita che un ciclo nei dati blocchi la pagina.
 */
export function catenePerId(nodi: readonly Nodo[]): CatenePerId {
  const perId = new Map(appiattisciAlberatura(nodi).map((nodo) => [nodo.id_nodo, nodo]));
  const catene = new Map<string, readonly string[]>();
  for (const nodo of perId.values()) {
    const etichette: string[] = [];
    let corrente: Nodo | undefined = nodo;
    const visti = new Set<string>();
    while (corrente && !visti.has(corrente.id_nodo)) {
      visti.add(corrente.id_nodo);
      etichette.unshift(corrente.denominazione);
      corrente = corrente.id_padre ? perId.get(corrente.id_padre) : undefined;
    }
    catene.set(nodo.id_nodo, etichette);
  }
  return catene;
}

/**
 * Percorso completo del nodo: «Sezione › Sottosezione › Obbligo».
 *
 * Se il nodo non e' nell'alberatura resta l'identificativo, che e' comunque
 * un'informazione: significa che il riferimento punta fuori dall'albero.
 */
export function percorsoNodo(catene: CatenePerId, idNodo: string): string {
  const catena = catene.get(idNodo);
  return catena && catena.length > 0 ? catena.join(SEPARATORE_PERCORSO) : idNodo;
}

/** Percorso dei soli antenati, senza il nodo. `null` per una sezione. */
export function percorsoAntenati(catene: CatenePerId, idNodo: string): string | null {
  const catena = catene.get(idNodo);
  if (!catena || catena.length < 2) return null;
  return catena.slice(0, -1).join(SEPARATORE_PERCORSO);
}

/** Percorso spezzato nelle due parti che l'interfaccia rende in modo diverso. */
export interface PartiPercorso {
  /** Antenati, senza separatore finale. `null` per una sezione. */
  antenati: string | null;
  /** Denominazione del nodo, o l'identificativo se il nodo non e' nell'albero. */
  denominazione: string;
}

/**
 * Percorso di un nodo diviso fra contesto e denominazione.
 *
 * Serve dove il percorso completo va mostrato ma il nome del nodo deve restare
 * leggibile: il contesto si rende in tono attenuato, la denominazione no.
 * Concatenate con {@link SEPARATORE_PERCORSO} le due parti ricostruiscono
 * esattamente {@link percorsoNodo} — l'accessibilita' non perde nulla rispetto
 * al percorso in chiaro, perche' entrambe le parti sono testo vero.
 *
 * Il contesto **non** e' ornamentale: fra i 121 obblighi ci sono due coppie
 * omonime, e per una delle due (`Sanzioni per mancata o incompleta
 * comunicazione dei dati`, sotto Organizzazione e sotto Personale) anche la
 * sottosezione madre ha lo stesso nome. Solo la catena intera distingue.
 */
export function partiPercorso(catene: CatenePerId, idNodo: string): PartiPercorso {
  const catena = catene.get(idNodo);
  if (!catena || catena.length === 0) return { antenati: null, denominazione: idNodo };
  return {
    antenati: catena.length > 1 ? catena.slice(0, -1).join(SEPARATORE_PERCORSO) : null,
    denominazione: catena[catena.length - 1],
  };
}

/** Una riga discendente dell'albero, con la sua posizione nella gerarchia. */
export interface RigaDiscendente {
  nodo: Nodo;
  /** Profondita' relativa al nodo di partenza: 1 per i figli diretti. */
  profondita: number;
  /**
   * Livello gerarchico **assoluto**, quello che `aria-level` deve annunciare.
   *
   * L'albero e' reso appiattito — i discendenti di una sezione stanno tutti in
   * un solo elenco, distinti dal rientro — quindi il livello non si puo'
   * dedurre dall'annidamento del DOM. Vale `profondita + 1` perche' il nodo di
   * partenza e' una sezione, cioe' il livello 1: i figli sono al 2, i nipoti al
   * 3. Prenderlo invece dal campo `livello` dell'API sarebbe sbagliato: quello
   * dice a che livello il nodo *dovrebbe* stare, `aria-level` deve descrivere
   * dove il nodo *sta* nella pagina.
   */
  livelloAria: number;
}

/**
 * Discendenti di un nodo in ordine di visita, con profondita' e livello ARIA.
 *
 * Mostrare i soli figli diretti farebbe sparire dalla pagina i 121 obblighi,
 * cioe' proprio i nodi su cui si pubblica.
 */
export function discendentiDiNodo(nodo: Nodo, profondita = 1): RigaDiscendente[] {
  return (nodo.figli ?? []).flatMap((figlio) => [
    { nodo: figlio, profondita, livelloAria: profondita + 1 },
    ...discendentiDiNodo(figlio, profondita + 1),
  ]);
}

/**
 * Voci di catalogo previste sotto un nodo e non ancora create.
 *
 * E' l'insieme che la modale offre in creazione e che il comando di
 * ripopolamento crea in blocco. Il calcolo sta qui perche' i due usi devono
 * lavorare sullo stesso insieme: un elenco e un pulsante che contano cose
 * diverse sono peggio del pulsante che manca.
 */
export function figliMancantiACatalogo(
  idPadre: string,
  figliEsistenti: readonly Pick<Nodo, "id_nodo">[],
): VoceCatalogo[] {
  const presenti = new Set(figliEsistenti.map((figlio) => figlio.id_nodo));
  return catalogoFigli(idPadre).filter((voce) => !presenti.has(voce.id_nodo));
}

/** Comandi ammessi su una riga dell'alberatura. */
export interface ComandiNodo {
  /** Il catalogo ANAC prevede figli per questo nodo: e' estendibile. */
  puoEstendere: boolean;
  /** Nodo senza figli e non di primo livello: e' rimovibile. */
  puoRimuovere: boolean;
  /** Etichetta del comando di estensione, coerente con il livello del padre. */
  etichettaEstensione: string;
}

/**
 * Comandi ammessi su un nodo.
 *
 * I due comandi sono **indipendenti** perche' rispondono a domande diverse:
 * estendere si legge dal catalogo ANAC, rimuovere dalla presenza di figli.
 * Legarli (chi non ha figli si puo' solo rimuovere) toglierebbe l'estensione
 * proprio a una sottosezione svuotata dei suoi obblighi, cioe' al caso in cui
 * serve di piu': il ripopolamento dal catalogo passa da li'.
 *
 * L'estendibilita' si legge dal catalogo, non dal livello: un nodo creato a
 * denominazione libera sotto «Altri contenuti → Dati ulteriori» non ha figli
 * previsti e non va offerto in estensione.
 */
export function comandiNodo(nodo: Pick<Nodo, "id_nodo" | "livello" | "figli">): ComandiNodo {
  return {
    puoEstendere: PADRI_A_CATALOGO.has(nodo.id_nodo),
    puoRimuovere: nodo.livello > 1 && (nodo.figli ?? []).length === 0,
    etichettaEstensione: nodo.livello === 1 ? "Aggiungi sottosezione" : "Aggiungi obbligo",
  };
}
